import { MDXProvider } from '@mdx-js/react'
import { Heading } from '@utils/Typography'
import { Link as GatsbyLink } from 'gatsby'
import { Text } from 'mds-web-ui'
import React, { useRef } from 'react'

import LanguageSelector from '../../WhatsNew/LanguageSelector'
import { SkipNavContent } from '../SkipLink'
import { HidePricing } from './HidePricing'
import { ActionsIcon, FlowIcon, TimepageIcon } from './Icon'
import Review from './Review'
import { Screenshot } from './Screenshot'
import { Tab, Tabs } from './Tabs'
import TOC from './TOC'
import Vimeo from './Vimeo'
import './style.css'
import { Box, Flex, Grid } from '@primitives/Layout'
import { fontSizes } from '@utils/Typography'
import { InlineLink } from '@/components/shared/InlineLink'
import { ArticleVimeo } from '@/components/utils/Vimeo'
import { getExploreAppsText } from '../../WhatsNew/exploreAppsTranslations'
import { ArticleMeta } from '../ArticleMeta'
import { Breadcrumb } from '../Breadcrumb'
import { ContactSupport } from '../ContactSupport'
import { Helpful } from '../Helpful'
import { RelatedArticles } from '../RelatedArticles'

const Link = ({ href, ...rest }) => {
  if (href.startsWith('/support/')) {
    return <GatsbyLink to={href} {...rest} />
  }

  return <a href={href} {...rest} />
}

const components = {
  Vimeo,
  TimepageIcon,
  ActionsIcon,
  FlowIcon,
  Tabs,
  Tab,
  Review,
  a: Link,
  Screenshot,
  HidePricing,
  ArticleVimeo
}

const DocsContent = ({
  title,
  children,
  category,
  section,
  app,
  slug,
  baseURL,
  tableOfContents,
  platforms,
  updatedFormatted,
  relatedArticles,
  faqs,
  showExploreApps = false,
  lang = 'en'
}) => {
  const isSupportArticle = Boolean(category)
  const sectionTitle = section?.title ?? section
  // Can pass in body (from page query) or children (pre-rendered)
  const ref = useRef(null)
  // useAutoplayVideos(ref)
  return (
    <>
      <SkipNavContent />

      <Grid
        as="section"
        ref={ref}
        width="100%"
        minWidth={0}
        alignItems="start"
        gridTemplateColumns={[
          'minmax(0, 1fr)',
          'minmax(0, 1fr)',
          'minmax(0, 1fr)',
          'minmax(0, 1fr) auto'
        ]}
        gridTemplateRows="repeat(3, auto)"
        gridTemplateAreas={[
          `"slug"
           "title"
           "toc"
           "content"`,
          `"slug"
           "title"
           "toc"
           "content"`,
          `"slug"
           "title"
           "toc"
           "content"`,
          `"slug slug"
           "title title"
           "content toc"`
        ]}
        gridColumnGap={[0, 4]}
        gridRowGap={2}
      >
        <Flex
          as="aside"
          css={{
            gridArea: 'slug'
          }}
          gap={1}
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          {isSupportArticle ? (
            <Breadcrumb
              category={category}
              section={section}
              title={title}
              slug={slug}
            />
          ) : (
            <Flex alignItems="baseline" gap={1}>
              {app ? (
                <>
                  <InlineLink
                    to={`/support/${app.toLowerCase()}/`}
                    fontSize={1}
                  >
                    {app}
                  </InlineLink>
                  <Text fontSize={1} color="#585859" opacity={0.5}>
                    {'>'}
                  </Text>
                </>
              ) : null}
              {sectionTitle ? <Text fontSize={1}>{sectionTitle}</Text> : null}
            </Flex>
          )}

          {slug && baseURL ? (
            <LanguageSelector baseURL={baseURL} slug={slug} />
          ) : null}
        </Flex>

        <Flex
          flexDirection="column"
          gap={1}
          style={{
            gridArea: 'title'
          }}
        >
          <Heading as="h1" fontSize={fontSizes.quote} lineHeight={1} m={0}>
            {title}
          </Heading>

          {isSupportArticle ? (
            <ArticleMeta
              platforms={platforms}
              updatedFormatted={updatedFormatted}
            />
          ) : null}
        </Flex>

        <TOC
          tableOfContents={tableOfContents}
          css={{
            gridArea: 'toc',
            width: 225
          }}
          pt={3}
        />

        <Flex
          gridArea="content"
          alignItems="flex-start"
          justifyContent="flex-start"
          flexDirection="column"
          maxWidth="75ch"
          width="100%"
          minWidth={0}
          as="article"
          css={{
            /* Parent of gatsby-resp-image-wrapper is often an inline span from MDX; give it a block context */
            '& :has(> .gatsby-resp-image-wrapper)': {
              display: 'block',
              width: '100%',
              maxWidth: '100%'
            },
            '& .gatsby-resp-image-wrapper': {
              marginLeft: 'initial !important',
              marginRight: 'initial !important'
            },
            '& .iphone .gatsby-resp-image-wrapper': {
              marginTop: '1.5em',
              marginBottom: '1.5em'
            },
            '& .iphone.portrait .gatsby-resp-image-wrapper': {
              // 300*650px image, 31px target border radius
              borderRadius: `${(31 / 300) * 100}%/${(31 / 650) * 100}% !important`
            },
            '& .iphone.landscape .gatsby-resp-image-wrapper': {
              borderRadius: `${(31 / 650) * 100}%/${(31 / 300) * 100}% !important`
            },
            '& .no-border .gatsby-resp-image-wrapper': {
              borderRadius: 0
            },
            '& .side-by-side': {
              display: 'flex',
              '& > span': {
                flex: 1
              },
              'span + span': {
                marginLeft: '8px !important'
              },
              '& > div': {
                flex: 1
              },
              'div + div': {
                marginLeft: '8px !important'
              }
            },
            '& .center': {
              marginLeft: 'auto',
              marginRight: 'auto'
            }
          }}
        >
          <MDXProvider components={components}>
            {children}

            {showExploreApps ? <ExploreApps lang={lang} /> : null}
          </MDXProvider>

          {isSupportArticle && faqs && faqs.length > 0 ? (
            <Box as="section" width="100%" mt={5}>
              <h2>Frequently asked questions</h2>
              {faqs.map((faq, i) => (
                <React.Fragment key={i}>
                  <h3>{faq.q}</h3>
                  <p>{faq.a}</p>
                </React.Fragment>
              ))}
            </Box>
          ) : null}

          {isSupportArticle ? (
            <>
              <RelatedArticles articles={relatedArticles} />
              <Helpful slug={slug} title={title} />
              <ContactSupport slug={slug} title={title} />
            </>
          ) : null}
        </Flex>
      </Grid>
    </>
  )
}

const ExploreApps = ({ lang = 'en' }) => {
  const { heading, apps } = getExploreAppsText(lang)
  const { actions, timepage, flow, overlap, bundle } = apps
  return (
    <Flex flexDirection="column" gap={3}>
      <h3>{heading}</h3>
      <Flex flexDirection="column" gap={3}>
        <Box>
          <Link href="/actions">Actions</Link>
          <span> — {actions}</span>
        </Box>
        <Box>
          <Link href="/timepage">Timepage</Link>
          <span> — {timepage}</span>
        </Box>
        <Box>
          <Link href="/flow">Flow</Link>
          <span> — {flow}</span>
        </Box>
        <Box>
          <Link href="/overlap">Overlap</Link>
          <span> — {overlap}</span>
        </Box>
        <Box>
          <Link href="/bundle">Bundle</Link>
          <span> — {bundle}</span>
        </Box>
      </Flex>
    </Flex>
  )
}

export default DocsContent
